import { useState, useEffect, useRef, useCallback } from 'react';
import type { ISlackUser, SlackConnectionStatus } from '@skystone/shared';
import { slackApi } from '@/services/slack-api';
import { Spinner } from '@/components/ui';
import { cn } from '@/lib/utils';

interface SlackUserSelectorProps {
  id?: string;
  onSelect: (user: ISlackUser) => void;
  value?: ISlackUser | null;
  disabled?: boolean;
  placeholder?: string;
  connectionStatus?: SlackConnectionStatus;
}

export function SlackUserSelector({
  id,
  onSelect,
  value = null,
  disabled = false,
  placeholder,
  connectionStatus,
}: Readonly<SlackUserSelectorProps>) {
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState<ISlackUser[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isDisconnected = connectionStatus !== undefined && connectionStatus !== 'connected';
  const isEffectivelyDisabled = disabled || isDisconnected;

  const resolvedPlaceholder = isDisconnected
    ? 'Slack not connected'
    : placeholder ?? 'Search Slack users...';

  // Fetch users from API
  const fetchUsers = useCallback(async (search: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await slackApi.getUsers({ search: search || undefined });
      setUsers(response.users);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load Slack users';
      setError(message);
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (!isOpen) return;

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchUsers(query);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, isOpen, fetchUsers]);

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && e.target instanceof Node && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset highlight when users list changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [users]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex < 0 || !listRef.current) return;
    const item = listRef.current.querySelector(`#slack-user-option-${highlightedIndex}`);
    if (item) {
      item.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  const handleInputFocus = () => {
    if (!isEffectivelyDisabled) {
      setIsOpen(true);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleSelect = (user: ISlackUser) => {
    onSelect(user);
    setQuery('');
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < users.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < users.length) {
          handleSelect(users[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        break;
    }
  };

  const handleClear = () => {
    setQuery('');
    onSelect(null as unknown as ISlackUser); // Signal clear to parent
    inputRef.current?.focus();
  };

  const dropdownId = 'slack-user-dropdown';

  const displayValue = value && !isOpen ? value.displayName || value.realName : query;

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Input field */}
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={isOpen ? dropdownId : undefined}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-activedescendant={isOpen && highlightedIndex >= 0 ? `slack-user-option-${highlightedIndex}` : undefined}
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          disabled={isEffectivelyDisabled}
          placeholder={resolvedPlaceholder}
          className={cn(
            'h-10 w-full rounded-md border border-input bg-background px-3 pr-8 text-sm text-foreground',
            'placeholder:text-muted-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50'
          )}
        />
        {/* Clear button when a value is selected */}
        {value && !isEffectivelyDisabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground"
            aria-label="Clear selection"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={listRef}
          id={dropdownId}
          aria-label="Slack users"
          className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-border bg-card shadow-lg"
        >
          {isLoading && (
            <div className="flex items-center justify-center py-4">
              <Spinner size="sm" />
            </div>
          )}

          {!isLoading && error && (
            <div className="px-3 py-2 text-sm text-error">{error}</div>
          )}

          {!isLoading && !error && users.length === 0 && (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              {query ? 'No users found' : 'Type to search...'}
            </div>
          )}

          {!isLoading &&
            !error &&
            users.map((user, index) => (
              <button
                type="button"
                key={user.slackId}
                id={`slack-user-option-${index}`}
                className={cn(
                  'flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left text-sm',
                  'hover:bg-accent hover:text-accent-foreground',
                  highlightedIndex === index && 'bg-accent text-accent-foreground',
                  value?.slackId === user.slackId && 'font-medium'
                )}
                aria-label={user.displayName || user.realName}
                onMouseEnter={() => setHighlightedIndex(index)}
                onMouseDown={(e) => {
                  // Prevent input blur before selection fires
                  e.preventDefault();
                }}
                onClick={() => handleSelect(user)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelect(user);
                  }
                }}
              >
                {/* Avatar */}
                {user.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt=""
                    className="h-8 w-8 shrink-0 rounded-full"
                  />
                ) : (
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground">
                    {(user.displayName || user.realName || '?').charAt(0).toUpperCase()}
                  </div>
                )}

                {/* Name + email */}
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-foreground">
                    {user.displayName || user.realName}
                  </p>
                  {user.email && (
                    <p className="truncate text-xs text-muted-foreground">
                      {user.email}
                    </p>
                  )}
                </div>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
